import { FormattedTime, useIntl } from 'react-intl';

import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Tooltip, Typography, paletteColors } from '@evaneos/b2b-ui';

import CallIcon from '@evaneos/b2b-ui/icons/CallOutlined';

import { PhoneCall as PhoneCallType } from '../../../../resources/phoning/types';
import { DossierId } from '../../../../resources/dossier/types';
import { formatDuration } from '../../../../shared/utilities/date';
import { useSidePanel } from '../../SidePanel/SidePanelContext';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';

import { usePhoneCallDisplayState } from '../../../../resources/phoning/hooks';

type PhoneCallCardProps = {
    discussionPhoneCall: PhoneCallType;
    shouldAutoOpen?: boolean;
};

export const PhoneCallCard = ({
    discussionPhoneCall,
    shouldAutoOpen = false,
}: PhoneCallCardProps) => {
    const { formatMessage } = useIntl();
    const { openPanel } = useSidePanel();
    const { dossierId } = useParams<{ dossierId: DossierId }>();

    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const phoneCallRef = useRef<HTMLDivElement>(null);

    const displayState = usePhoneCallDisplayState(discussionPhoneCall);

    useEffect(() => {
        if (shouldAutoOpen && phoneCallRef.current) {
            const timeout = window.setTimeout(() => {
                phoneCallRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });

                if (displayState.transcriptState === 'ready') {
                    openPanel({ contentType: 'phone-call-details', data: discussionPhoneCall });
                }
            }, 50);

            return () => {
                window.clearTimeout(timeout);
            };
        }
    }, [shouldAutoOpen, displayState.transcriptState, openPanel, discussionPhoneCall]);

    const handleOpenSummary = () => {
        if (displayState.transcriptState === 'ready') {
            trackEvent((ampli, defaultProperties) => {
                ampli.transcriptViewed({
                    ...defaultProperties,
                    ...trackedTripProperties,
                    call_id: discussionPhoneCall.id.toString(),
                    synthesis_content: 'ok',
                    click_source: 'discussion',
                });
            });

            openPanel({ contentType: 'phone-call-details', data: discussionPhoneCall });
        }
    };

    const getButtonTooltip = () => {
        if (displayState.transcriptState === 'none') {
            return formatMessage({ id: 'dossier.discussion.phone_call.not_recorded_tooltip' });
        }

        if (displayState.transcriptState === 'loading') {
            return formatMessage({
                id: 'dossier.discussion.phone_call.summary_generation_in_progress',
            });
        }

        return '';
    };

    const isButtonDisabled = displayState.transcriptState !== 'ready';

    return (
        <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}
            ref={phoneCallRef}
        >
            <Box
                component="article"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'border.main',
                    borderRadius: 4,
                    flexWrap: 'wrap',
                    width: '100%',
                    maxWidth: 470,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        component="span"
                        sx={{
                            backgroundColor: paletteColors.green['900'],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                        }}
                    >
                        <CallIcon fontSize="small" sx={{ color: paletteColors.neutral[0] }} />
                    </Box>
                    <Box>
                        <Typography fontWeight="bold" mb={0.5}>
                            {formatMessage({
                                id: 'dossier.discussion.phone_call.voice_call_header',
                            })}
                        </Typography>
                        <Typography variant="body2">
                            {formatDuration(discussionPhoneCall.duration)}
                        </Typography>
                    </Box>
                </Box>

                <Box marginLeft="auto">
                    <Tooltip
                        title={getButtonTooltip()}
                        disableInteractive
                        disableHoverListener={!isButtonDisabled}
                        placement="top"
                        slotProps={{
                            popper: {
                                modifiers: [
                                    {
                                        name: 'offset',
                                        options: {
                                            offset: [0, -12],
                                        },
                                    },
                                ],
                            },
                        }}
                    >
                        <div>
                            <Button
                                variant="outlined"
                                onClick={handleOpenSummary}
                                disabled={isButtonDisabled}
                            >
                                {formatMessage({
                                    id: 'dossier.discussion.phone_call.view_summary_button',
                                })}
                            </Button>
                        </div>
                    </Tooltip>
                </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">
                <FormattedTime value={discussionPhoneCall.sendDate} />
            </Typography>
        </Box>
    );
};
